import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Autocomplete,
  Chip,
  Alert,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { createSection, updateSection } from 'shared/utils/proposalApi';

const SECTION_ERROR_MAP = {
  'The fields proposal, section_key must make a unique set.': 'This section key is already used by another section in this proposal. Please choose a different key.',
};

const formatSectionError = (error) => {
  if (typeof error === 'string') return error;
  if (error?.non_field_errors?.length) {
    const msg = error.non_field_errors[0];
    return SECTION_ERROR_MAP[msg] || msg;
  }
  const fieldErrors = Object.entries(error)
    .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
    .join(' | ');
  return fieldErrors || 'An unexpected error occurred.';
};

const SectionDialog = ({ open, onClose, proposalId, section, sections, onSuccess, consortiumMembers = [], defaultParentId = null }) => {
  const isEdit = Boolean(section);

  const [title, setTitle] = useState('');
  const [sectionKey, setSectionKey] = useState('');
  const [position, setPosition] = useState('');
  const [parent, setParent] = useState('');
  const [wordLimit, setWordLimit] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [isWorkPlan, setIsWorkPlan] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (section) {
      setTitle(section.title || '');
      setSectionKey(section.section_key || '');
      setPosition(section.position ?? '');
      setParent(section.parent || '');
      setWordLimit(section.word_limit || '');
      setDueDate(section.due_date || '');
      setIsWorkPlan(Boolean(section.is_work_plan));
      if (section.assignees?.length > 0) {
        const mapped = section.assignees
          .map(id => consortiumMembers.find(u => u?.id === id))
          .filter(Boolean);
        setAssignees(mapped);
      } else {
        setAssignees([]);
      }
    } else {
      setTitle('');
      setSectionKey('');
      setPosition('');
      setParent(defaultParentId || '');
      setWordLimit('');
      setDueDate('');
      setIsWorkPlan(false);
      setAssignees([]);
    }
    setError(null);
  }, [section, open, consortiumMembers, defaultParentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setIsSaving(true);
    setError(null);

    const submitData = {
      proposal: proposalId,
      title: title.trim(),
      assignees: assignees.map(a => a.id),
      parent: parent || null,
      ...(isEdit && {
        section_key: sectionKey,
        position: position !== '' ? parseInt(position) : undefined,
        word_limit: wordLimit ? parseInt(wordLimit) : null,
        due_date: dueDate || null,
        is_work_plan: isWorkPlan,
      }),
    };

    const result = isEdit
      ? await updateSection(section.id, submitData)
      : await createSection(submitData);

    if (result.success) {
      onSuccess?.(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    setIsSaving(false);
  };

  // Recursively flatten sections tree (top-level + all subsections)
  const flattenSections = (list) => {
    const result = [];
    for (const s of list || []) {
      result.push(s);
      if (s.subsections?.length) result.push(...flattenSections(s.subsections));
    }
    return result;
  };
  const allSections = flattenSections(sections);

  // Collect all descendant IDs of the current section to prevent cycles
  const getDescendantIds = (id, list) => {
    const ids = new Set();
    const recurse = (targetId) => {
      for (const s of list) {
        if (String(s.parent) === String(targetId)) {
          ids.add(s.id);
          recurse(s.id);
        }
      }
    };
    recurse(id);
    return ids;
  };
  const descendantIds = section ? getDescendantIds(section.id, allSections) : new Set();

  const workPlanTakenByOther = allSections.some(
    s => s.is_work_plan && s.id !== section?.id
  );

  const appendixSection = allSections.find(s => s.section_key === 'appendix');
  const parentOptions = allSections.filter(
    s => s.id !== section?.id && !descendantIds.has(s.id) && s.section_key !== 'appendix'
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>{isEdit ? 'Edit Section' : 'Add Section'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>

          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Parent Section (Optional)</InputLabel>
            <Select
              value={parent}
              onChange={e => setParent(e.target.value)}
              label="Parent Section (Optional)"
              MenuProps={{ sx: { zIndex: 3001 } }}
            >
              <MenuItem value="">None (Top Level)</MenuItem>
              {parentOptions.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.section_key ? (
                    <span style={{ marginRight: 8, fontSize: '0.75rem', color: '#6b7280' }}>
                      [{s.section_key}]
                    </span>
                  ) : null}
                  {s.title}
                </MenuItem>
              ))}
              {appendixSection && (
                <MenuItem value={appendixSection.id} sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 1 }}>
                  <span style={{ marginRight: 8, fontSize: '0.75rem', color: '#6b7280' }}>[appendix]</span>
                  Move to Appendix
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {isEdit && (
            <>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Section Key"
                  value={sectionKey}
                  onChange={e => setSectionKey(e.target.value)}
                  helperText="Unique identifier (e.g. excellence, methodology)"
                  fullWidth
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Position"
                  type="number"
                  value={position}
                  onChange={e => setPosition(e.target.value)}
                  fullWidth
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Stack>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 1, opacity: workPlanTakenByOther ? 0.5 : 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isWorkPlan}
                      onChange={e => setIsWorkPlan(e.target.checked)}
                      size="small"
                      disabled={workPlanTakenByOther}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Work Plan</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {workPlanTakenByOther
                          ? 'Another section is already marked as the Work Plan. Unmark it first.'
                          : 'This section represents the Work Plan — it will be replaced by the full work-packages block in preview and document export.'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <TextField
                label="Word Limit (Optional)"
                type="number"
                value={wordLimit}
                onChange={e => setWordLimit(e.target.value)}
                helperText="Maximum word count for this section"
                fullWidth
              />

              <TextField
                label="Due Date (Optional)"
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </>
          )}

          <Autocomplete
            multiple
            options={consortiumMembers.filter(u => u?.id)}
            getOptionLabel={option => {
              if (!option) return '';
              const name = option.name || option.email || 'Unknown';
              return option.org_short_name ? `${name} (${option.org_short_name})` : name;
            }}
            slotProps={{ popper: { style: { zIndex: 3001 } } }}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            value={assignees}
            onChange={(_, v) => setAssignees(v)}
            renderInput={params => (
              <TextField
                {...params}
                label="Assignees (Optional)"
                placeholder="Select users responsible for this section"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option?.id || index}
                  label={option?.name || option?.email || 'Unknown'}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
          />

          {error && (
            <Alert severity="error">
              {formatSectionError(error)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || !title.trim()}
        >
          {isSaving ? <CircularProgress size={20} /> : (isEdit ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SectionDialog;
